import {
  ianaTimezoneSchema,
  parseValidationFailureDetails,
  signupRequestSchema,
  type SignupRequest,
} from '@instanexus/contracts';
import {
  errorMessage,
  textPolicyMessage,
  useTermsAgreement,
  useTranslation,
} from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Button,
  ProviderButton,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Checkbox,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PhoneInput,
  TermsAgreementText,
  TextLink,
} from '@instanexus/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { CaptchaField } from '@/components/captcha-field';
import { useApiClient } from '@/hooks/use-api-client';
import { useCaptcha } from '@/hooks/use-captcha';
import { centralLoginPath, verifyEmailPath } from '@/lib/auth-paths';
import { readFederatedErrorCode } from '@/lib/federated-return';
import { startGoogleSignUp } from '@/lib/google-sign-up';
import { markPortalAsNew } from '@/lib/portal-cookie';

/** Same fallback reasoning as `login-page.tsx` — never a declared `ErrorCode`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

/** Full-page navigation; a function so the unit test can observe it. */
function redirectTo(url: string): void {
  window.location.assign(url);
}

/**
 * The contract minus the organization URL. The form no longer asks for one: the
 * API derives a unique slug from the organization name when the request carries
 * none, so a person signing up never meets a "that address is taken" refusal for
 * a box they had no reason to care about.
 *
 * There is no "type it twice" box either. A mistyped password costs one trip
 * through "Forgot password", which every account already has; a second box cost
 * every signup a field.
 */
const signupFormSchema = signupRequestSchema.omit({ tenantSlug: true, captchaToken: true });

/**
 * The API type says `acceptTerms: true`; the form holds a plain boolean (unchecked by default)
 * and the schema above refuses anything but `true`.
 */
type SignupFormValues = Omit<SignupRequest, 'tenantSlug' | 'acceptTerms' | 'captchaToken'> & {
  acceptTerms: boolean;
};

/** The left-hand pitch, in display order. Hidden below `md`, where the card is one column. */
const BENEFIT_KEYS = [
  'signup.benefitKiosk',
  'signup.benefitNotify',
  'signup.benefitBadges',
  'signup.benefitAgreements',
] as const;

/** Every path this form can attach a server field error to. */
const FORM_FIELDS = [
  'name',
  'email',
  'password',
  'tenantName',
  'phone',
  'acceptTerms',
] as const satisfies readonly (keyof SignupFormValues)[];

/**
 * The fields are controlled, so an untouched optional field holds `''` rather
 * than `undefined`, and a pasted address routinely carries whitespace.
 * Normalising in front of the schema means the SAME value is validated and sent.
 *
 * `phone` is DROPPED when empty rather than sent as `''`: the contract's phone is
 * `string | undefined` and an empty string fails its E.164 regex, so a form that
 * sent one would report an invalid number to a person who entered none.
 */
/**
 * This browser's IANA zone, or `undefined` if it is not one the contract accepts.
 *
 * Signup sends it so the `<Tenant> HQ` location it auto-creates is born in the
 * right zone instead of being corrected later. It is NOT a form field: nobody
 * signing up wants to be asked what timezone they are in, and the browser
 * already knows.
 *
 * Guarded rather than trusted. `resolvedOptions().timeZone` is whatever the
 * host's ICU build says, and a browser that answers with a link name, an offset
 * or nothing at all must cost the user their account creation — not a 400 on a
 * field they never saw. When it does not parse we simply omit it and the API
 * falls back to UTC, which step 1 of the wizard then offers to correct.
 */
function resolveBrowserTimezone(): string | undefined {
  const parsed = ianaTimezoneSchema.safeParse(Intl.DateTimeFormat().resolvedOptions().timeZone);
  return parsed.success ? parsed.data : undefined;
}

function normalizeValues(values: SignupFormValues): SignupFormValues {
  const phone = values.phone?.trim() ?? '';
  return {
    name: values.name.trim(),
    email: values.email.trim(),
    password: values.password,
    tenantName: values.tenantName.trim(),
    acceptTerms: values.acceptTerms,
    ...(phone.length > 0 ? { phone } : {}),
  };
}

function isBranch(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Rewrites every message the resolver produced into this app's translated copy.
 *
 * Same technique as the central reset-password page: zod's own prose is untranslated
 * English and is never shown (docs/011). The organization name is the one field
 * with a more specific message, because the text policy names what it refused.
 * Every other field gets an empty message: `FormMessage` renders nothing for it, and
 * the red `aria-invalid` border marks the box.
 */
function localizeErrors(
  errors: unknown,
  policyMessage: (code: string) => string | null,
  termsMessage: string,
): void {
  if (!isBranch(errors)) return;
  for (const [name, error] of Object.entries(errors)) {
    if (!isBranch(error)) continue;
    const original = error['message'];
    const policy =
      name === 'tenantName' && error['type'] === 'custom' && typeof original === 'string'
        ? policyMessage(original)
        : null;
    error['message'] = name === 'acceptTerms' ? termsMessage : (policy ?? '');
  }
}

/**
 * Self-serve signup: a stranger creates a tenant and an owner account for it
 * (S26 Phase 3).
 *
 * Public by construction — nobody who can already sign in needs it — so it is
 * registered outside `RequireAuth`, next to `/login`.
 *
 * THE PHONE FIELD IS ALWAYS RENDERED AND ALWAYS OPTIONAL HERE. Whether a
 * deployment demands a verified number is `SIGNUP_PHONE_VERIFICATION`, which is
 * server-side configuration this SPA cannot read; the alternative — a public
 * `/api/web/config` endpoint published for one boolean — is a new unauthenticated
 * surface and is deliberately not built. A deployment that requires a number
 * answers `VALIDATION_FAILED` with `phone` in `details.fieldErrors`, which lands
 * on the field below exactly like any other field refusal.
 *
 * `EMAIL_ALREADY_REGISTERED` goes to its field rather than to the page-level
 * alert: the whole point of that code is that the person knows which box to
 * change. Every other code goes to the alert.
 *
 * LABELS LIVE INSIDE THE BOXES. Each field keeps a real `<label>` — visually
 * hidden — and shows the same string as its placeholder, so the accessible name
 * every test and screen reader uses is unchanged while the card loses a row per
 * field. This is the auth-card exception in docs/018 "Form layout"; it is not a
 * pattern for forms inside the app shell.
 *
 * Its states: idle, submitting (`formState.isSubmitting`, which disables the
 * button so a double-click cannot create two tenants), error (a field message, a
 * page-level translated message, or both), and done — which is a navigation to
 * `/verify-email` rather than a card, because nothing on this screen is still
 * useful once the account exists.
 */
export function SignupPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();

  // A refusal the Google callback passed back through router state.
  const [errorCode, setErrorCode] = useState<string | null>(readFederatedErrorCode(location.state));

  // A failed or empty providers answer just means no button; the password form is never blocked.
  const providers = useQuery({
    queryKey: ['federation', 'providers'],
    queryFn: () => api.federation.providers(),
    staleTime: Infinity,
    retry: false,
  });
  const offersGoogle = providers.data?.providers.includes('google') ?? false;
  const [isStartingGoogle, setIsStartingGoogle] = useState(false);

  /** For a refusal the server addressed to a field; client-side misses only turn the box red. */
  const validationMessage = errorMessage(t, 'VALIDATION_FAILED');
  const agreement = useTermsAgreement();
  const termsRequiredMessage = agreement.requiredMessage;

  const resolver = useMemo<Resolver<SignupFormValues>>(() => {
    // The schema's `acceptTerms` is `z.literal(true)`; the form holds a boolean and the schema
    // is what refuses `false`, so the resolver is retyped to the form's own values.
    const base = zodResolver(signupFormSchema) as unknown as Resolver<SignupFormValues>;
    return async (values, context, options) => {
      const result = await base(normalizeValues(values), context, {
        criteriaMode: options.criteriaMode,
        fields: options.fields,
        shouldUseNativeValidation: options.shouldUseNativeValidation,
      });
      localizeErrors(
        result.errors,
        (code) => textPolicyMessage(t, 'custom', code),
        termsRequiredMessage,
      );
      return result;
    };
  }, [termsRequiredMessage, t]);

  const captcha = useCaptcha();

  const form = useForm<SignupFormValues>({
    resolver,
    defaultValues: {
      name: '',
      email: '',
      password: '',
      tenantName: '',
      phone: '',
      acceptTerms: false,
    },
  });

  const onSubmit = form.handleSubmit(async (values: SignupFormValues): Promise<void> => {
    setErrorCode(null);
    if (captcha.token === null) return;
    const captchaToken = captcha.token;
    const { acceptTerms: _checked, ...request } = normalizeValues(values);
    const timezone = resolveBrowserTimezone();

    try {
      const { channels } = await api.auth.signup({
        ...request,
        acceptTerms: true,
        captchaToken,
        ...(timezone === undefined ? {} : { timezone }),
      });
      /**
       * The number travels in the router's location state, not in this
       * navigation's URL: `/verify-email` has no use for it beyond handing it to
       * `/verify-phone`, and putting it in a query string one screen early would
       * publish it into browser history for no benefit. Carried only when the
       * server actually issued an SMS code.
       */
      markPortalAsNew();
      const handoff =
        channels.includes('SMS') && request.phone !== undefined ? { phone: request.phone } : null;
      await navigate(verifyEmailPath(request.email), {
        ...(handoff === null ? {} : { state: handoff }),
      });
    } catch (error: unknown) {
      const code = isApiError(error) ? error.code : UNEXPECTED_FAILURE;

      if (code === 'EMAIL_ALREADY_REGISTERED') {
        form.setError('email', { type: 'server', message: errorMessage(t, code) });
        return;
      }

      // A refusal the server addressed to a specific control — `phone` under a
      // deployment that requires one is the case this exists for. Read through
      // `parseValidationFailureDetails`, never by indexing into `details`.
      const failure = isApiError(error) ? parseValidationFailureDetails(error.details) : null;
      const named = FORM_FIELDS.filter((field) => failure?.fieldErrors[field] !== undefined);
      if (named.length > 0) {
        for (const field of named) {
          // The server's issue messages are the raw policy codes for a text-policy refusal.
          const policy =
            field === 'tenantName'
              ? textPolicyMessage(t, 'custom', failure?.fieldErrors[field]?.[0] ?? '')
              : null;
          form.setError(field, { type: 'server', message: policy ?? validationMessage });
        }
        return;
      }

      setErrorCode(code);
    } finally {
      // Single-use token: re-arm after every attempt, whatever happened.
      captcha.reset();
    }
  });

  const isSubmitting = form.formState.isSubmitting;

  const continueWithGoogle = async (): Promise<void> => {
    setErrorCode(null);
    setIsStartingGoogle(true);
    try {
      await startGoogleSignUp(api, form.getValues('email'), redirectTo);
    } catch (e: unknown) {
      setErrorCode(isApiError(e) ? e.code : UNEXPECTED_FAILURE);
      setIsStartingGoogle(false);
    }
  };

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md md:grid md:max-w-3xl md:grid-cols-2">
        <CardHeader className="md:border-border md:gap-2 md:border-r md:pb-6">
          <h1 className="text-2xl font-semibold text-primary text-balance">{t('signup.title')}</h1>
          <CardDescription>{t('signup.description')}</CardDescription>
          <ul className="mt-4 hidden flex-col gap-3 md:flex">
            {BENEFIT_KEYS.map((key) => (
              <li key={key} className="text-primary flex items-start gap-2 text-sm text-pretty">
                <Check aria-hidden="true" className="text-success mt-0.5 size-4 shrink-0" />
                {t(key)}
              </li>
            ))}
          </ul>
        </CardHeader>

        <Form {...form}>
          <form
            noValidate
            className="md:pt-6"
            onSubmit={(event) => {
              void onSubmit(event);
            }}
          >
            <CardContent className="flex flex-col gap-3">
              {errorCode === null ? null : (
                <p role="alert" className="text-danger text-sm text-pretty">
                  {errorMessage(t, errorCode)}
                </p>
              )}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('signup.nameLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="name"
                        placeholder={t('signup.nameLabel')}
                        width="full"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('signup.emailLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        autoCapitalize="none"
                        spellCheck={false}
                        placeholder={t('signup.emailLabel')}
                        width="full"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tenantName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('signup.tenantNameLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="organization"
                        placeholder={t('signup.tenantNameLabel')}
                        width="full"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('signup.phoneLabel')}</FormLabel>
                    <FormControl>
                      {/* The platform's one phone control (`@instanexus/ui`,
                          S26-TASK-007) — never a second country list. */}
                      <PhoneInput
                        name={field.name}
                        ref={field.ref}
                        onBlur={field.onBlur}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder={t('signup.phoneLabel')}
                        label={t('signup.phoneLabel')}
                        countryLabel={t('signup.phoneCountryLabel')}
                        searchLabel={t('signup.phoneSearchLabel')}
                        searchPlaceholder={t('signup.phoneSearchPlaceholder')}
                        emptyMessage={t('signup.phoneEmptyMessage')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('signup.passwordLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        placeholder={t('signup.passwordLabel')}
                        width="full"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      {t('signup.passwordHint')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="acceptTerms"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-start gap-2">
                      <FormControl>
                        <Checkbox
                          name={field.name}
                          ref={field.ref}
                          checked={field.value}
                          onBlur={field.onBlur}
                          onCheckedChange={(checked) => {
                            field.onChange(checked === true);
                          }}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal text-pretty">
                        <TermsAgreementText
                          message={agreement.checkboxMessage}
                          termsUrl={agreement.termsUrl}
                          privacyUrl={agreement.privacyUrl}
                        />
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <CaptchaField captcha={captcha} />
            </CardContent>

            <CardFooter className="flex flex-col items-stretch gap-2">
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || captcha.token === null}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? t('signup.submitting') : t('signup.submit')}
              </Button>
              {offersGoogle ? (
                <>
                  <p className="text-center text-sm text-muted-foreground">{t('federated.or')}</p>
                  <ProviderButton
                    provider="google"
                    disabled={isStartingGoogle || isSubmitting}
                    aria-busy={isStartingGoogle}
                    onClick={() => {
                      void continueWithGoogle();
                    }}
                  >
                    {isStartingGoogle
                      ? t('federated.redirecting')
                      : t('federated.continueWithGoogle')}
                  </ProviderButton>
                </>
              ) : null}
              <p className="text-subtle text-center text-xs text-pretty">{t('signup.phoneHint')}</p>
              <p className="text-subtle text-center text-sm text-pretty">
                {t('signup.haveAccount')}{' '}
                <TextLink asChild>
                  <Link to={centralLoginPath('admin')}>{t('signup.signIn')}</Link>
                </TextLink>
              </p>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </AuthCardLayout>
  );
}
